import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { Mail, Lock, ArrowRight, KeyRound } from '../lib/icons';
import { supabase, resetPassword } from '../lib/supabase';
import { acceptHomeInvitation } from '../lib/household';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isResetMode, setIsResetMode] = useState(false);
  const inviteToken = searchParams.get('invite');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    // Check if user arrived from password reset email
    if (searchParams.get('reset') === 'true') {
      setSuccess('You can now set your new password below.');
    }
  }, [searchParams]);

  const handleInputChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    setError('');
    setSuccess('');
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;
      if (inviteToken) {
        await acceptHomeInvitation(inviteToken);
        navigate('/profile');
        return;
      }
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await resetPassword(formData.email);
      setSuccess('Check your email for password reset instructions.');
      setFormData({ ...formData, email: '' });
      setIsResetMode(false);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.formContainer}>
        <View style={styles.header}>
          <Text style={styles.title}>
            {isResetMode ? 'Reset Password' : 'Welcome back'}
          </Text>
          <View style={styles.subtitleContainer}>
            {inviteToken && !isResetMode ? (
              <Text style={styles.inviteText}>
                Sign in or create an account to join the shared household.
              </Text>
            ) : null}
            {isResetMode ? (
              <Pressable onPress={() => setIsResetMode(false)}>
                <Text style={styles.linkText}>Back to login</Text>
              </Pressable>
            ) : (
              <View style={styles.subtitleRow}>
                <Text style={styles.subtitle}>
                  Don't have an account?{' '}
                </Text>
                <Pressable onPress={() => navigate(inviteToken ? `/onboarding?invite=${inviteToken}` : '/onboarding')}>
                  <Text style={styles.linkText}>Sign up</Text>
                </Pressable>
              </View>
            )}
          </View>
        </View>

        <View style={styles.form}>
          <View style={styles.inputGroup}>
            <View style={styles.inputContainer}>
              <Text style={styles.label}>Email</Text>
              <View style={styles.inputWrapper}>
                <View style={styles.iconContainer}>
                  <Mail size={20} color="#9ca3af" />
                </View>
                <TextInput
                  style={styles.input}
                  value={formData.email}
                  onChangeText={(value) => handleInputChange('email', value)}
                  placeholder="Enter your email"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                />
              </View>
            </View>

            {!isResetMode && (
              <View style={styles.inputContainer}>
                <Text style={styles.label}>Password</Text>
                <View style={styles.inputWrapper}>
                  <View style={styles.iconContainer}>
                    <Lock size={20} color="#9ca3af" />
                  </View>
                  <TextInput
                    style={styles.input}
                    value={formData.password}
                    onChangeText={(value) => handleInputChange('password', value)}
                    placeholder="Enter your password"
                    secureTextEntry
                    autoCapitalize="none"
                    autoComplete="password"
                  />
                </View>
              </View>
            )}
          </View>

          {error && (
            <Text style={styles.errorText}>{error}</Text>
          )}

          {success && (
            <View style={styles.successContainer}>
              <Text style={styles.successText}>{success}</Text>
            </View>
          )}

          <View style={styles.buttonGroup}>
            <Pressable
              onPress={isResetMode ? handlePasswordReset : handleLogin}
              disabled={loading}
              style={({ pressed }) => [
                styles.submitButton,
                (loading || pressed) && styles.submitButtonDisabled
              ]}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : isResetMode ? (
                <View style={styles.buttonContent}>
                  <KeyRound size={16} color="#ffffff" />
                  <Text style={styles.buttonText}>Send Reset Instructions</Text>
                </View>
              ) : (
                <View style={styles.buttonContent}>
                  <Text style={styles.buttonText}>Sign in</Text>
                  <ArrowRight size={16} color="#ffffff" />
                </View>
              )}
            </Pressable>

            {!isResetMode && (
              <Pressable
                onPress={() => setIsResetMode(true)}
                style={styles.forgotButton}
              >
                <Text style={styles.forgotButtonText}>Forgot your password?</Text>
              </Pressable>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web' ? { minHeight: 'var(--app-height)' as any } : { minHeight: '100%' }),
    backgroundColor: '#f9fafb',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 48,
    paddingHorizontal: 16,
    ...(Platform.OS === 'web' && {
      paddingHorizontal: 24,
    }),
  },
  formContainer: {
    width: '100%',
    maxWidth: 448,
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
  },
  subtitleContainer: {
    marginTop: 8,
  },
  subtitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  subtitle: {
    fontSize: 14,
    color: '#4b5563',
  },
  inviteText: {
    fontSize: 13,
    color: '#4b5563',
    textAlign: 'center',
    marginBottom: 6,
  },
  linkText: {
    fontWeight: '500',
    color: '#000000',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
  form: {
    marginTop: 32,
    gap: 24,
  },
  inputGroup: {
    gap: 16,
  },
  inputContainer: {
    marginBottom: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  inputWrapper: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    position: 'absolute',
    left: 12,
    zIndex: 1,
  },
  input: {
    flex: 1,
    paddingLeft: 40,
    paddingRight: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    fontSize: 16,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  errorText: {
    color: '#ef4444',
    fontSize: 14,
    textAlign: 'center',
  },
  successContainer: {
    backgroundColor: '#f0fdf4',
    padding: 12,
    borderRadius: 6,
  },
  successText: {
    color: '#16a34a',
    fontSize: 14,
    textAlign: 'center',
  },
  buttonGroup: {
    gap: 16,
  },
  submitButton: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 6,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
  },
  forgotButton: {
    width: '100%',
  },
  forgotButtonText: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
});

export default Login;
